"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { ICONS } from "@artha/icons";
import { ProfileCard } from "@artha/ui";
import {
  FacebookIcon,
  InstagramIcon,
  XIcon,
  LinkedInIcon,
  TikTokIcon,
  MediumIcon,
  SubstackIcon,
  WebsiteIcon,
  YouTubeIcon
} from "./SocialIcons";
import { SocialAuthProvider, type CreatorProfileForMyLink } from "@artha/types";
import { getOptimizedImageUrl } from "@/lib/utils";

type ProfileHeaderProps = {
  creator: CreatorProfileForMyLink;
  username: string;
  colors: {
    profileCard: string;
    titleColor: string;
    textColor: string;
  };
  onSocialClick?: (provider: string) => void;
};

const formatFollowers = (count?: number | null) =>
  typeof count === "number"
    ? new Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(count)
    : "";



export function ProfileHeader({ creator, username, colors, onSocialClick }: ProfileHeaderProps) {
  const t = useTranslations("profile");

  const optimizedAvatarUrl = useMemo(
    () => getOptimizedImageUrl(creator.avatarUrl, { width: 160, quality: 75 }),
    [creator.avatarUrl]
  );

  const profileStats = useMemo(() => {
    const accounts = creator.socialAccounts ?? [];
    const iconFor = (provider: string) => {
      const normalizedProvider = provider.toLowerCase();
      switch (normalizedProvider) {
        case SocialAuthProvider.FACEBOOK.toLowerCase():
          return <FacebookIcon className="h-4 w-4" />;
        case SocialAuthProvider.INSTAGRAM.toLowerCase():
          return <InstagramIcon className="h-4 w-4" />;
        case SocialAuthProvider.TWITTER.toLowerCase():
        case "x":
          return <XIcon className="h-4 w-4" />;
        case SocialAuthProvider.LINKEDIN.toLowerCase():
          return <LinkedInIcon className="h-4 w-4" />;
        case SocialAuthProvider.TIKTOK.toLowerCase():
          return <TikTokIcon className="h-4 w-4" />;
        case SocialAuthProvider.YOUTUBE.toLowerCase():
          return <YouTubeIcon className="h-4 w-4" />;
        case "medium":
          return <MediumIcon className="h-4 w-4" />;
        case "substack":
          return <SubstackIcon className="h-4 w-4" />;
        case "website":
          return <WebsiteIcon className="h-4 w-4" />;
        default:
          return (
            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-[#101828] text-[10px] font-bold uppercase tracking-widest text-[#101828]">
              {provider[0]?.toUpperCase() ?? "?"}
            </span>
          );
      }
    };

    type SocialAccount = NonNullable<CreatorProfileForMyLink["socialAccounts"]>[number];

    const urlFor = (account: SocialAccount) => {
      if (account.profileUrl) return account.profileUrl;
      if (account.providerUserId?.startsWith("http")) return account.providerUserId;
      const handle = account.handle?.replace(/^@/, "");
      if (!handle) return undefined;
      switch (account.provider.toLowerCase()) {
        case SocialAuthProvider.INSTAGRAM:
          return `https://www.instagram.com/${handle}`;
        case SocialAuthProvider.TIKTOK:
          return `https://www.tiktok.com/@${handle}`;
        case SocialAuthProvider.TWITTER:
        case "x":
          return `https://x.com/${handle}`;
        case SocialAuthProvider.FACEBOOK:
          return `https://www.facebook.com/${handle}`;
        case SocialAuthProvider.YOUTUBE:
          return `https://www.youtube.com/@${handle}`;
        case "substack":
          return `https://${handle}.substack.com`;
        case "medium":
          return `https://medium.com/@${handle}`;
        default:
          return undefined;
      }
    };

    const stats = accounts.map((account) => ({
      label:
        account.provider.toLowerCase() === "x"
          ? "X"
          : account.provider.charAt(0).toUpperCase() + account.provider.slice(1),
      value:
        typeof account.followersCount === "number"
          ? formatFollowers(account.followersCount)
          : "",
      icon: iconFor(account.provider),
      url: urlFor(account),
      provider: account.provider.toLowerCase(),
    }));

    if (stats.length > 0) return stats;
    if (typeof creator.totalFollowers === "number") {
      return [
        {
          label: t("followersLabel"),
          value: formatFollowers(creator.totalFollowers),
          icon: <ICONS.WEBSITE className="h-7 w-7 text-[#667085]" />,
        },
      ];
    }
    return [];
  }, [creator, t]);

  return (
    <section className="creator-profile-section w-full">
      <ProfileCard
        name={creator.displayName || username}
        subtitle={creator.bio || t("noBio")}
        avatar={optimizedAvatarUrl || creator.avatarUrl || undefined}
        backgroundColor={colors.profileCard}
        titleColor={colors.titleColor}
        textColor={colors.textColor}
        stats={profileStats}
        onStatClick={(stat) => {
          if (!stat.provider) return;
          onSocialClick?.(stat.provider);
        }}
        nameTag="h1"
        className="creator-profile-card"
      />
    </section>
  );
}
